import { createSelector } from 'reselect';

import { createKeySelector } from 'Lib/reduxUtils';

import { STATE_LOADING, STATE_SUCCESS } from 'Store/commonActions';

import { REDUCER_KEY_LANGUAGE, REDUCER_KEY_MESSAGE } from './constants';
import type { I18nState } from './types';

/**
 * Gets internationalization namespace from the state
 *
 * @param  {Object} state - top-level state
 * @return {Object} internationalization reducer
 */
export const selectInternationalization = createKeySelector('internationalization');

/**
 * Gets the current language from the internationalization state
 *
 * @param  {Object} state - internationalization state
 * @return {string} language
 */
export const getLanguage = (state: I18nState) => state[REDUCER_KEY_LANGUAGE];

/**
 * Gets language message loading status
 *
 * @param  {ReducerType} state - internationalization state
 * @return {number} loading state
 */
export const getLanguageMessageStatus = createSelector(
  selectInternationalization,
  (state) => state[REDUCER_KEY_MESSAGE]
);

/**
 * Gets whether this is the language is loaded
 *
 * @param  {Object} state - top-level state
 * @return {boolean} true when the initial internationalization has not yet loaded
 */
export const getIsLanguageLoaded = createSelector(
  getLanguageMessageStatus,
  (status) => status === STATE_SUCCESS
);

export const getIsLanguageLoading = createSelector(
  getLanguageMessageStatus,
  (status) => status === STATE_LOADING
);

/**
 * Gets the current language from the root state
 *
 * @param  {Object} state - top-level state
 * @return {string} language
 */
export const getCurrentLanguage = createSelector(selectInternationalization, getLanguage);
